import React, { useEffect, useState } from "react";
import "./SessionDetailsPage.css";
import "../../../patientFullView/patientFullView.css";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Widgets from "../../../../widgets";
import Icons from "../../../../Assets/Icons";
import strings from "../../../../utils/strings";
import jsPDF from "jspdf";
import { getNurseSessionDetails, getRequestSessions, scheduleNursingRequest, getNurseRequestDetails, getSessionReassignHistory } from "../../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import { getNurseDetails } from "../../../../modules/NursignCare/ManageNurse/ManageNurseThunk";
import ToastPopup from "../../../../widgets/Toast/toastPopup";
import { buildFileUrl, getFileType } from "../../../../utils/fileHelpers";
import { openSecureFileUrl } from "../../../../utils/secureFilePreview";
import ChatWidget from "../../../Chat/ChatWidget";
import PatientPersonalInfo from "../../../patientFullView/patientPersonalInfo";

const DAY_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Convert 12h {h, m, p} to minutes-since-midnight for comparison
const to24hMinutes = (h, m, p) => {
  let hrs = parseInt(h, 10);
  if (p === "PM" && hrs !== 12) hrs += 12;
  if (p === "AM" && hrs === 12) hrs = 0;
  return hrs * 60 + parseInt(m, 10);
};

// Inline time picker: dropdown hour/minute/AM-PM selection
// minMinutes: optional minutes-since-midnight floor — options before this are disabled (used to block past times for "today")
const TimePicker = ({ value, onChange, minMinutes = null }) => {
  const parse = (v) => {
    if (!v) return { h: "10", m: "00", p: "AM" };
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
      const d = new Date(v);
      const hrs = d.getHours();
      return { h: String(hrs % 12 || 12).padStart(2, "0"), m: String(d.getMinutes()).padStart(2, "0"), p: hrs >= 12 ? "PM" : "AM" };
    }
    const match = v.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match) return { h: match[1].padStart(2, "0"), m: match[2], p: match[3].toUpperCase() };
    return { h: "10", m: "00", p: "AM" };
  };

  const { h, m, p } = parse(value);
  const upd = (nh, nm, np) => onChange(`${nh}:${nm} ${np}`);
  const sel = {
    className: "sd-time-select",
    style: { border: "1px solid var(--box-border)", borderRadius: "0.6rem", padding: "0.4rem 0.6rem", fontSize: "1.4rem", background: "var(--main-bg)", cursor: "pointer" },
  };
  const isDisabled = (nh, nm, np) => minMinutes !== null && to24hMinutes(nh, nm, np) < minMinutes;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
      <select {...sel} value={h} onChange={(e) => upd(e.target.value, m, p)}>
        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((v) => (
          <option key={v} value={v} disabled={isDisabled(v, m, p)}>
            {v}
          </option>
        ))}
      </select>
      <span style={{ fontWeight: 700 }}>:</span>
      <select {...sel} value={m} onChange={(e) => upd(h, e.target.value, p)}>
        {["00", "15", "30", "45"].map((v) => (
          <option key={v} value={v} disabled={isDisabled(h, v, p)}>
            {v}
          </option>
        ))}
      </select>
      <select {...sel} value={p} onChange={(e) => upd(h, m, e.target.value)}>
        <option value="AM" disabled={isDisabled(h, m, "AM")}>
          AM
        </option>
        <option value="PM" disabled={isDisabled(h, m, "PM")}>
          PM
        </option>
      </select>
    </div>
  );
};

const SessionDetailsPage = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("vn") || i18n.language?.startsWith("vi") ? "vi" : "en";
  const nav = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const queryParams = new URLSearchParams(location.search);
  const sessionId = queryParams.get("id");
  const requestId = queryParams.get("requestId");
  const from = queryParams.get("from") || "upcoming";
  const returnTo = queryParams.get("returnTo");

  const { sessionDetails, requestSessions, requestDetails } = useSelector((state) => state.manageRequest);
  const [localSession, setLocalSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);

  const breadcrumbs = ["nursingCare", "manageRequests", "sessionDetails"];
  const backTab = from === "scheduled" ? "scheduled" : from === "past" ? "past" : "upcoming";
  const paths = [strings.navigation.manageNurseReq, `${strings.navigation.manageNurseReq}?activeList=${backTab}`, strings.navigation.viewSessionDetails];
  const handleBack = () => {
    if (returnTo) {
      nav(decodeURIComponent(returnTo));
      return;
    }
    nav(-1);
  };

  // Editable check-in / check-out (stored as "HH:MM AM/PM")
  // Check-in / check-out (upcoming/past)
  const [checkInTime, setCheckInTime] = useState("10:00 AM");
  const [checkOutTime, setCheckOutTime] = useState("01:00 PM");

  // Per-service schedule state for scheduled tab — one entry per service
  // times: array of {sH, sM, sP, eH, eM, eP} — separate fields so each input is independent
  const defaultSlot = () => {
    const now = new Date();
    const hrs = now.getHours();
    const mins = now.getMinutes();
    const period = hrs >= 12 ? "PM" : "AM";
    const h12 = String(hrs % 12 || 12).padStart(2, "0");
    const mm = String(mins).padStart(2, "0");
    // End time = start + 1 hour
    const eHrs = (hrs + 1) % 24;
    const ePeriod = eHrs >= 12 ? "PM" : "AM";
    const eH12 = String(eHrs % 12 || 12).padStart(2, "0");
    return { sH: h12, sM: mm, sP: period, eH: eH12, eM: mm, eP: ePeriod };
  };
  const defaultServiceSchedule = (perTime = 1) => ({
    startDate: "",
    endDate: "",
    times: Array.from({ length: Math.max(1, perTime) }, defaultSlot),
    activeSlot: 0,
    frequencyDays: Array(7).fill(false),
  });
  const [serviceSchedules, setServiceSchedules] = useState([defaultServiceSchedule()]);

  // Parse "HH:MM AM/PM" string into slot parts
  const parseTimeStr = (raw) => {
    const m = (raw || "").match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    return m ? { h: m[1], min: m[2], p: m[3].toUpperCase() } : { h: "", min: "", p: "AM" };
  };
  // Ensure every schedule entry has a times array with the new {sH,sM,sP,eH,eM,eP} shape
  const normalizeSched = (s) => {
    if (!s.times) {
      return { ...s, times: [defaultSlot()], activeSlot: 0 };
    }
    // Migrate old {startTime, endTime} slots to new shape
    const times = s.times.map((slot) => {
      if (slot.sH !== undefined) return slot; // already new shape
      const st = parseTimeStr(slot.startTime);
      const et = parseTimeStr(slot.endTime);
      return { sH: st.h, sM: st.min, sP: st.p, eH: et.h, eM: et.min, eP: et.p };
    });
    return { ...s, times };
  };

  const updateServiceSchedule = (idx, updates) =>
    setServiceSchedules((prev) => prev.map((s, i) => (i === idx ? { ...normalizeSched(s), ...updates } : s)));

  // Today's date (YYYY-MM-DD) in local timezone — toISOString() uses UTC and can return yesterday
  const todayISO = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
  // Current minutes-since-midnight — used to disable past times when the selected date is today
  const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();

  // Legacy single-service refs (used by upcoming/past tabs only)
  const [schedStartDate, setSchedStartDate] = useState("");
  const [schedEndDate, setSchedEndDate] = useState("");
  const [schedStartTime, setSchedStartTime] = useState("10:00 AM");
  const [schedEndTime, setSchedEndTime] = useState("01:00 PM");
  const [frequencyDays, setFrequencyDays] = useState(Array(7).fill(false));

  // Nurse details fetched from nurse API
  const [nurseDetails, setNurseDetails] = useState(null);
  const [nurseLoading, setNurseLoading] = useState(false);

  const [sessionRating, setSessionRating] = useState(null);
  const [sessionUserRating, setSessionUserRating] = useState(null);
  // Nurse ID to open in the chat widget when "Chat with Nurse" is clicked
  const [chatNurseId, setChatNurseId] = useState(null);

  // Nurse reassignment history — Upcoming Session Details only
  const [reassignHistory, setReassignHistory] = useState([]);
  const [reassignHistoryLoading, setReassignHistoryLoading] = useState(false);

  useEffect(() => {
    if (from !== "upcoming" || !sessionId) {
      setReassignHistory([]);
      return;
    }
    setReassignHistoryLoading(true);
    dispatch(getSessionReassignHistory({ sessionId })).then((res) => {
      setReassignHistoryLoading(false);
      if (res.meta.requestStatus === "fulfilled") {
        const list = res.payload?.data?.history || res.payload?.data || [];
        const entries = Array.isArray(list) ? list : [];
        setReassignHistory([...entries].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      }
    });
  }, [sessionId, from]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch and store session/request data in local state
  useEffect(() => {
    setLocalSession(null);
    setSessionLoading(true);

    if (sessionId) {
      // Upcoming / Past: fetch full session by ID
      dispatch(getNurseSessionDetails({ id: sessionId })).then((r) => {
        if (r.meta.requestStatus === "fulfilled") setLocalSession(r.payload?.data?.session || null);
        setSessionRating(r.payload?.data?.rating || null);
        setSessionUserRating(r.payload?.data?.userRating || null);
        setSessionLoading(false);
      });
    } else if (requestId) {
      // Scheduled: fetch request details, populate form from all request.services
      dispatch(getNurseRequestDetails({ id: requestId })).then((r) => {
        if (r.meta.requestStatus === "fulfilled") {
          const req = r.payload?.data?.request || null;
          if (req) {
            setLocalSession({
              _id: null,
              nursingRequest: { _id: req._id, nursingRequestID: req.nursingRequestID },
              services: req.services || [],
              date: req.startDate || null,
              status: req.status,
              checkInTime: null,
              checkOutTime: null,
              frequencyMap: req.frequencyMap || null,
            });
          }
        }
        setSessionLoading(false);
      });
    }
  }, [sessionId, requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Always use local state
  const session = localSession;

  // Initialize one schedule entry per service when services load, and re-init if per_time changes
  useEffect(() => {
    if (from !== "scheduled") return;
    const svcs = requestDetails?.services?.length ? requestDetails.services : session?.services?.length ? session.services : session ? [{}] : [];
    if (!svcs.length) return;
    setServiceSchedules((prev) => {
      let changed = prev.length !== svcs.length;
      const next = svcs.map((svc, i) => {
        const perTime = Math.max(1, svc?.per_time || 1);
        const existing = prev[i];
        if (!existing) { changed = true; return defaultServiceSchedule(perTime); }
        const existingTimes = existing.times?.length || 1;
        if (existingTimes !== perTime) { changed = true; return { ...existing, times: Array.from({ length: perTime }, (_, ti) => existing.times?.[ti] || defaultSlot()) }; }
        return existing;
      });
      return changed ? next : prev;
    });
  }, [requestDetails?.services, session?.services, from]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-sync frequency checkboxes when both dates are selected (per service)
  useEffect(() => {
    serviceSchedules.forEach((sched, idx) => {
      if (!sched.startDate || !sched.endDate) return;
      const s = new Date(sched.startDate);
      const e = new Date(sched.endDate);
      const daySet = new Set();
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const jsDay = d.getDay();
        const dayIdx = jsDay === 0 ? 6 : jsDay - 1;
        daySet.add(dayIdx);
      }
      const newDays = Array.from({ length: 7 }, (_, i) => daySet.has(i));
      if (JSON.stringify(newDays) !== JSON.stringify(sched.frequencyDays)) {
        updateServiceSchedule(idx, { frequencyDays: newDays });
      }
    });
  }, [serviceSchedules.map((s) => s.startDate + s.endDate).join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  // If any service's start date is today and any slot's start time is in the past, clear it
  useEffect(() => {
    serviceSchedules.forEach((sched, idx) => {
      if (sched.startDate !== todayISO) return;
      const normalized = normalizeSched(sched);
      const newTimes = normalized.times.map((slot) => {
        if (!slot.sH || !slot.sM) return slot;
        if (to24hMinutes(slot.sH, slot.sM, slot.sP) < nowMinutes) {
          return { ...slot, sH: "", sM: "" };
        }
        return slot;
      });
      updateServiceSchedule(idx, { times: newTimes });
    });
  }, [serviceSchedules.map((s) => s.startDate).join("|")]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch full request details for the fullDetails UI
  useEffect(() => {
    const reqId = session?.nursingRequest?._id;
    if (reqId) dispatch(getNurseRequestDetails({ id: reqId }));
  }, [session?.nursingRequest?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync check-in / check-out and frequency from session data
  useEffect(() => {
    if (!session) return;
    setCheckInTime(session.checkInTime || "10:00 AM");
    setCheckOutTime(session.checkOutTime || "01:00 PM");
    // Seed frequency map if it exists and is 7 chars
    if (session.frequencyMap && session.frequencyMap.length === 7) {
      setFrequencyDays(session.frequencyMap.split("").map((c) => c === "1"));
    }
  }, [session?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Extract nurse from session (nurse is an array)
  const nurseEntry = Array.isArray(session?.nurse) ? session.nurse[0] : session?.nurse;
  const nurseId = nurseEntry?._id;

  // Seed nurseDetails from embedded session data immediately (has name, ID, phone, gender, photo)
  // Then fetch full nurse API for email / address
  useEffect(() => {
    if (!nurseEntry) return;
    // Seed immediately from session data
    setNurseDetails(
      (prev) =>
        prev || {
          firstName: nurseEntry.firstName,
          lastName: nurseEntry.lastName,
          employeeID: nurseEntry.employeeID,
          phoneNumber: nurseEntry.phoneNumber,
          countryCode: nurseEntry.countryCode,
          gender: nurseEntry.gender,
          profilePhoto: nurseEntry.profilePhoto,
        },
    );
    // Fetch full details for email + address
    if (nurseId) {
      setNurseLoading(true);
      dispatch(getNurseDetails(nurseId))
        .then((result) => {
          if (result.meta.requestStatus === "fulfilled") {
            const data = result.payload?.data?.nurse || result.payload?.nurse || result.payload;
            if (data) setNurseDetails(data);
          }
        })
        .finally(() => setNurseLoading(false));
    }
  }, [nurseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect ISO date strings ("2026-06-01T04:30:00.000Z")
  const isISO = (s) => s && /^\d{4}-\d{2}-\d{2}T/.test(s);

  // Format date — handles ISO and pre-formatted ("23 Jun 2026 at 10:00 AM")
  const formatSessionDate = (s) => {
    if (!s) return "-";
    if (isISO(s)) {
      const d = new Date(s);
      return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    }
    return s.includes(" at ") ? s.split(" at ")[0] : s;
  };

  // Format time — handles ISO and pre-formatted
  const formatSessionTime = (s) => {
    if (!s) return "-";
    if (isISO(s)) {
      const d = new Date(s);
      return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    }
    return s.includes(" at ") ? s.split(" at ")[1] : s;
  };

  // Duration — works for both ISO and "HH:MM AM/PM" strings
  const calcDuration = (start, end) => {
    if (!start || !end) return "";
    try {
      if (isISO(start) && isISO(end)) {
        const diff = Math.round((new Date(end) - new Date(start)) / 60000);
        if (diff <= 0) return "";
        const h = Math.floor(diff / 60),
          m = diff % 60;
        return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
      }
      const extract = (s) => (s.includes(" at ") ? s.split(" at ")[1] : s);
      const toMins = (str) => {
        const [time, period] = str.trim().split(" ");
        const [h, m] = time.split(":").map(Number);
        let hrs = h;
        if (period?.toUpperCase() === "PM" && h !== 12) hrs += 12;
        if (period?.toUpperCase() === "AM" && h === 12) hrs = 0;
        return hrs * 60 + (m || 0);
      };
      const diff = toMins(extract(end)) - toMins(extract(start));
      if (diff <= 0) return "";
      const h = Math.floor(diff / 60),
        m = diff % 60;
      return h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
    } catch {
      return "";
    }
  };

  const resolveLocalized = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") return val.en || val.vi || Object.values(val).find((v) => typeof v === "string") || "";
    return String(val);
  };

  // Formats an originalNurses/replacementNurses entry (single nurse, array of nurses, or plain string) for display
  const formatNurseNames = (nurses) => {
    const arr = Array.isArray(nurses) ? nurses : nurses ? [nurses] : [];
    const names = arr
      .map((n) => {
        if (!n) return null;
        if (typeof n === "string") return n;
        return `${resolveLocalized(n.firstName)} ${resolveLocalized(n.lastName)}`.trim() || n.employeeID || n._id || null;
      })
      .filter(Boolean);
    return names.length ? names.join(", ") : "-";
  };

  const formatHistoryDateTime = (dateTime) => {
    if (!dateTime) return "-";
    const d = new Date(dateTime);
    if (isNaN(d)) return String(dateTime);
    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    return `${date} at ${time}`;
  };

  // Nurse name from session data (available before full fetch)
  const nurseNameFromSession = nurseEntry ? `${resolveLocalized(nurseEntry.firstName)} ${resolveLocalized(nurseEntry.lastName)}`.trim() : "";

  const displayNurseName = nurseDetails
    ? `${resolveLocalized(nurseDetails.firstName)} ${resolveLocalized(nurseDetails.lastName)}`.trim()
    : nurseNameFromSession || "-";

  // Convert "10:00 AM" / ISO → "10:00" (24h) for API
  const to24h = (s) => {
    if (!s) return "10:00";
    if (isISO(s)) {
      const d = new Date(s);
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
    const [time, period] = s.trim().split(" ");
    const [hStr, mStr] = time.split(":");
    let h = parseInt(hStr);
    if (period?.toUpperCase() === "PM" && h !== 12) h += 12;
    if (period?.toUpperCase() === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${mStr || "00"}`;
  };

  // Extract "YYYY-MM-DD" from ISO or formatted date string
  const toISODate = (s) => {
    if (!s) return "";
    if (isISO(s)) return s.split("T")[0];
    try {
      const d = new Date(s);
      if (!isNaN(d)) return d.toISOString().split("T")[0];
    } catch {}
    return "";
  };

  // 7-digit preview (Mon-Sun) for display only — legacy, used by upcoming/past tabs
  const frequencyMapStr = frequencyDays.map((d) => (d ? "1" : "0")).join("");

  // Build frequencyMap string for a date range given a 7-bool frequencyDays array
  const buildFrequencyMap = (start, end, days) => {
    const activeDays = days || frequencyDays;
    if (!start || !end) return activeDays.map((d) => (d ? "1" : "0")).join("") || "1";
    const s = new Date(start),
      e = new Date(end);
    let result = "";
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      const jsDay = d.getDay();
      const idx = jsDay === 0 ? 6 : jsDay - 1;
      result += activeDays[idx] ? "1" : "0";
    }
    return result.length > 0 ? (result.includes("1") ? result : result.replace(/0/g, "1")) : "1";
  };

  const handleSave = () => {
    // For scheduled: use requestId from URL; for upcoming/past: use session's nursing request ID
    const reqId = requestId || session?.nursingRequest?._id;
    if (!reqId) {
      ToastPopup.error(t("requestIdNotFound"));
      return;
    }

    if (from === "scheduled") {
      for (let i = 0; i < serviceSchedules.length; i++) {
        const sched = normalizeSched(serviceSchedules[i]);

        // Validate dates present
        if (!sched.startDate || !sched.endDate) {
          ToastPopup.error(t("selectStartEndDates"));
          return;
        }

        // Validate all slots have times filled
        for (let si = 0; si < sched.times.length; si++) {
          const slot = sched.times[si];
          if (!slot.sH || !slot.sM || !slot.eH || !slot.eM) {
            ToastPopup.error(`Service ${i + 1}: please fill all appointment times (slot ${si + 1})`);
            return;
          }
        }

        // Validate no overlapping slots within the same service
        const toMins = (h, m, p) => {
          let hrs = parseInt(h, 10);
          if (p === "PM" && hrs !== 12) hrs += 12;
          if (p === "AM" && hrs === 12) hrs = 0;
          return hrs * 60 + parseInt(m, 10);
        };
        const intervals = sched.times.map((slot) => ({
          start: toMins(slot.sH, slot.sM, slot.sP),
          end: toMins(slot.eH, slot.eM, slot.eP),
        }));
        for (let a = 0; a < intervals.length; a++) {
          if (intervals[a].end <= intervals[a].start) {
            ToastPopup.error(`Service ${i + 1}: slot ${a + 1} end time must be after start time`);
            return;
          }
          for (let b = a + 1; b < intervals.length; b++) {
            if (intervals[a].start < intervals[b].end && intervals[b].start < intervals[a].end) {
              ToastPopup.error(`Service ${i + 1}: slot ${a + 1} and slot ${b + 1} times overlap`);
              return;
            }
          }
        }
      }
    }

    // For upcoming/past: derive from session
    const fallbackStartDate = toISODate(session?.date || session?.startTime);
    const fallbackEndDate = toISODate(session?.date || session?.endTime) || fallbackStartDate;
    const fallbackStartTime = to24h(session?.startTime);
    const fallbackEndTime = to24h(session?.endTime);

    const srcServices = requestDetails?.services?.length
      ? requestDetails.services
      : session?.services?.length
      ? session.services
      : session
      ? [{ service: session.service, subService: session.subService, serviceItem: session.serviceItem }]
      : [];

    // Build services array — expand date range × per_time slots into individual {serviceDate, startTime, endTime} entries
    const slotToTimeStr = (h, m, p) => `${h || "00"}:${m || "00"} ${p || "AM"}`;
    const expandDates = (startDate, endDate, freqDays) => {
      if (!startDate || !endDate) return startDate ? [startDate] : [];
      const dates = [];
      const s = new Date(startDate);
      const e = new Date(endDate);
      for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
        const jsDay = d.getDay();
        const idx = jsDay === 0 ? 6 : jsDay - 1; // Mon(0)..Sun(6)
        if (!freqDays || freqDays[idx]) {
          dates.push(d.toISOString().split("T")[0]);
        }
      }
      return dates;
    };

    const sessionServices = srcServices.length
      ? srcServices.flatMap((svc, i) => {
          const sched = from === "scheduled" ? normalizeSched(serviceSchedules[i] || serviceSchedules[0]) : null;
          const serviceId = svc.service?._id || (typeof svc.service === "string" ? svc.service : "");
          const subServiceId = svc.subService?._id || (typeof svc.subService === "string" ? svc.subService : "");
          const serviceItemId = svc.serviceItem?._id || (typeof svc.serviceItem === "string" ? svc.serviceItem : "");

          if (!sched) {
            return [{ service: serviceId, subService: subServiceId, serviceItem: serviceItemId, serviceDate: fallbackStartDate, startTime: fallbackStartTime, endTime: fallbackEndTime }];
          }

          const dates = expandDates(sched.startDate, sched.endDate, sched.frequencyDays);
          // For each active date × each time slot → one entry
          return dates.flatMap((serviceDate) =>
            sched.times.map((slot) => ({
              service: serviceId,
              subService: subServiceId,
              serviceItem: serviceItemId,
              serviceDate,
              startTime: to24h(slotToTimeStr(slot.sH, slot.sM, slot.sP)),
              endTime: to24h(slotToTimeStr(slot.eH, slot.eM, slot.eP)),
            }))
          );
        })
      : [];

    const body = { services: sessionServices };

    dispatch(scheduleNursingRequest({ requestId: reqId, body })).then((result) => {
      if (result.meta.requestStatus === "fulfilled") {
        ToastPopup.success(t("requestScheduled"));
        nav(`${strings.navigation.manageNurseReq}?activeList=${backTab}`);
      } else {
        ToastPopup.error(result.error?.message || t("failedToSchedule"));
      }
    });
  };

  // Build request data for fullDetails UI (same structure as ApprovedRequestDetails)
  const addr = requestDetails?.address;
  const fmtAddr = (a) => (a ? [a.street, a.ward, a.city, a.province, a.postalCode].filter(Boolean).join(", ") : "");
  const reqData = {
    firstName: requestDetails?.patient?.fullName || "",
    lastName: "",
    name: requestDetails?.patient?.fullName || "",
    patientName: requestDetails?.patient?.fullName || "",
    profilePhoto: requestDetails?.patient?.profilePhoto || "",
    gender: requestDetails?.patient?.gender || "",
    dateOfBirth: requestDetails?.patient?.dateOfBirth || "",
    dob: requestDetails?.patient?.dateOfBirth || "",
    hnNumber: requestDetails?.patient?.hnNumber || "",
    hn: requestDetails?.patient?.hnNumber || "",
    phone: requestDetails?.patient?.countryCode ? `+${requestDetails.patient.countryCode} ${requestDetails.patient.phoneNumber}` : requestDetails?.patient?.phoneNumber || "",
    phoneNumber: requestDetails?.patient?.phoneNumber || "",
    countryCode: requestDetails?.patient?.countryCode || "",
    email: requestDetails?.patient?.email || "",
    insuranceType: requestDetails?.patient?.insuranceCategory || "",
    insuranceCategory: requestDetails?.patient?.insuranceCategory || "",
    address: fmtAddr(addr),
    distanceInKm: requestDetails?.distanceInKm || "",
    zone: requestDetails?.distanceInKm ? `${requestDetails.distanceInKm} km` : "",
    patientCondition: requestDetails?.patientCondition || "",
    status: requestDetails?.status || "",
    attachments: (requestDetails?.attachments || []).filter(Boolean).map((a) => {
      if (typeof a === "string") {
        const name = a.split("/").pop() || "file";
        return { type: getFileType({ name }), url: buildFileUrl(a), name };
      }
      const rawUrl = a.url || a.path || a.fileUrl || a.filePath || a.location || "";
      const name = a.name || rawUrl.split("/").pop() || "file";
      const type = getFileType({ name, mimeType: a.mimeType || a.mimetype || a.contentType || a.type });
      return { type, url: buildFileUrl(rawUrl), name };
    }),
    description: requestDetails?.description || "",
    nursePreferences: { languages: requestDetails?.languagePreferences || [], gender: (requestDetails?.genderPreferences || [])[0] || "" },
    quotation: requestDetails?.quotationURL ? [{ type: "pdf", name: "Quotation", url: requestDetails.quotationURL }] : [],
  };

  const feedbackRating = Number(session?.feedback?.rating) || 0;
  const normalizedFeedbackRating = Math.min(Math.max(feedbackRating, 0), 5);
  const feedbackComment = session?.feedback?.comment || "-";
  const allObservationImages = [...(session?.medicalUploadedImages || []), ...(session?.checkoutAttachments || [])];
  const apiObservationImages = allObservationImages
    .map((image) => {
      if (typeof image === "string") return buildFileUrl(image);
      return buildFileUrl(image?.url || image?.fileUrl || image?.filePath || image?.path || image?.location || "");
    })
    .filter(Boolean);
  const observationImages = apiObservationImages;
  const nurseNotesText = session?.checkoutNotes || session?.notes || "";
  const hasNurseObservations = from !== "scheduled" && (apiObservationImages.length > 0 || nurseNotesText || from === "past" || session?.status === "CHECKED_OUT");
  // Build per-service entries. The session API returns parallel arrays:
  // session.service[], session.subService[], session.serviceItem[]
  const currentSessionServices = (() => {
    if (session?.services?.length) return session.services;
    if (!session) return [];
    // Zip parallel top-level arrays into per-service objects
    const svcs = Array.isArray(session.service) ? session.service : session.service ? [session.service] : [null];
    const subSvcs = Array.isArray(session.subService) ? session.subService : session.subService ? [session.subService] : [null];
    const items = Array.isArray(session.serviceItem) ? session.serviceItem : session.serviceItem ? [session.serviceItem] : [null];
    const len = Math.max(svcs.length, subSvcs.length, items.length);
    return Array.from({ length: len }, (_, i) => ({
      service: svcs[i] || null,
      subService: subSvcs[i] || null,
      serviceItem: items[i] || null,
    }));
  })();
const handleDownloadObservations = async () => {
  const pdf = new jsPDF();

  let yPosition = 20;

  pdf.setFontSize(16);
  pdf.text("Homecare Staff Observations", 20, yPosition);
  yPosition += 12;

  // Patient details
  pdf.setFontSize(12);
  const patientLine = [
    reqData.patientName ? `Patient: ${reqData.patientName}` : null,
    reqData.hn ? `HN: ${reqData.hn}` : null,
    reqData.phone ? `Phone: ${reqData.phone}` : null,
  ].filter(Boolean).join("   |   ");
  if (patientLine) {
    pdf.text(patientLine, 20, yPosition);
    yPosition += 8;
  }

  // Homecare staff details
  const staffName = displayNurseName && displayNurseName !== "-" ? displayNurseName : null;
  const staffId = nurseDetails?.employeeID || nurseEntry?.employeeID || null;
  const staffLine = [
    staffName ? `Staff: ${staffName}` : null,
    staffId ? `ID: ${staffId}` : null,
  ].filter(Boolean).join("   |   ");
  if (staffLine) {
    pdf.text(staffLine, 20, yPosition);
    yPosition += 8;
  }

  // Session date
  const sessionDateStr = session?.date ? new Date(session.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : null;
  if (sessionDateStr) {
    pdf.text(`Session Date: ${sessionDateStr}`, 20, yPosition);
    yPosition += 8;
  }

  yPosition += 5;

  // Add Images
  for (let i = 0; i < observationImages.length; i++) {
    try {
      const imageUrl = observationImages[i];

      const response = await fetch(imageUrl);
      const blob = await response.blob();

      const reader = new FileReader();

      const imageData = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });


      if (yPosition > 220) {
        pdf.addPage();
        yPosition = 20;
      }


      pdf.setFontSize(12);
      pdf.text(
        `Observation Image ${i + 1}`,
        20,
        yPosition
      );

      yPosition += 10;


      pdf.addImage(
        imageData,
        "JPEG",
        20,
        yPosition,
        160,
        100
      );


      yPosition += 115;

    } catch(error) {
      console.error(
        "Image download failed",
        error
      );
    }
  }


  // Add Notes
  if (nurseNotesText) {

    if (yPosition > 230) {
      pdf.addPage();
      yPosition = 20;
    }


    pdf.setFontSize(14);

    pdf.text(
      "Homecare Staff Notes",
      20,
      yPosition
    );


    yPosition += 10;


    pdf.setFontSize(11);


    const notes = pdf.splitTextToSize(
      nurseNotesText,
      170
    );


    pdf.text(
      notes,
      20,
      yPosition
    );
  }


  pdf.save(
    "homecare-staff-observations.pdf"
  );
};

  return (
    <>
      <div className="pdl-container session-details-wrapper">
        <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />

        {/* Header */}
        <div className="department-heading pdl">
          <div>{t("visitDetails")}</div>
          <div className="pdl-buttons">
            <div className="pdl-btn verified pav-status" style={{ backgroundColor: "#e6fffa", color: "#00b894", border: "none" }}>
              {t(session?.status) || session?.status || "-"}
            </div>
            <span className="req-id-text">
              {t("request_id")}: {session?.nursingRequest?.nursingRequestID || "-"}
            </span>
          </div>
        </div>

        {/* ── Scheduled tab: Session Details UI — one block per service ── */}
        {from === "scheduled" && (
          <div className="pdl-info">
            <div className="pdl-header-row">
              <p className="pdl-title">{t("sessionDetails")}</p>
            </div>

            {(requestDetails?.services?.length ? requestDetails.services : session?.services?.length ? session.services : session ? [{ service: session.service, subService: session.subService, serviceItem: session.serviceItem }] : []).map((svc, idx, arr) => {
              const sched = normalizeSched(serviceSchedules[idx] || defaultServiceSchedule());
              return (
                <div key={idx} style={{ marginTop: idx > 0 ? "2rem" : "0.8rem", paddingTop: idx > 0 ? "2rem" : 0, borderTop: idx > 0 ? "1px solid var(--box-border)" : "none" }}>
                  {/* Service row */}
                  <div className="service-type-main2" style={{ marginLeft: "2.2rem", marginTop: idx === 0 ? "0.8rem" : "3rem" }}>
                    {arr.length > 1 && <div className="service-serial">{idx + 1}.</div>}
                    <div className="service-input-box">
                      <span>{t("serviceGroup")}</span>
                      <input type="text" className="form-section-input-2" value={svc?.service?.title?.[lang] || svc?.service?.title?.en || ""} readOnly />
                    </div>
                    <div className="service-input-box">
                      <span>{t("subServiceGroup")}</span>
                      <input type="text" className="form-section-input-2" value={svc?.subService?.title?.[lang] || svc?.subService?.title?.en || ""} readOnly />
                    </div>
                    <div className="service-input-box">
                      <span>{t("serviceItem")}</span>
                      <input type="text" className="form-section-input-2" value={svc?.serviceItem?.title?.[lang] || svc?.serviceItem?.title?.en || ""} readOnly />
                    </div>
                    {svc?.per_time != null && (
                      <div className="service-input-box">
                        <span>{t("perTime") || "Per Time"}</span>
                        <input type="text" className="form-section-input-2" value={svc.per_time} readOnly />
                      </div>
                    )}
                  </div>

                  {/* Start Date / End Date / Appointment Time */}
                  <div className="pdl-body grid-3-col" style={{ marginTop: "2rem" }}>
                    <div className="field-item">
                      <span className="session-heading" style={{ color: "#009688" }}>
                        {t("startDate")}<span className="asterik">*</span>
                      </span>
                      <input
                        type="date"
                        value={sched.startDate}
                        min={todayISO}
                        onChange={(e) => updateServiceSchedule(idx, { startDate: e.target.value })}
                        style={{ height: "3.6rem", padding: "0 1rem", borderRadius: "2rem", border: "1px solid var(--box-border)", background: "var(--main-bg)", fontSize: "1.3rem", width: "100%", outline: "none", cursor: "pointer" }}
                      />
                    </div>
                    <div className="field-item">
                      <span className="session-heading" style={{ color: "#009688" }}>
                        {t("endDate")}<span className="asterik">*</span>
                      </span>
                      <input
                        type="date"
                        value={sched.endDate}
                        min={sched.startDate || todayISO}
                        onChange={(e) => updateServiceSchedule(idx, { endDate: e.target.value })}
                        style={{ height: "3.6rem", padding: "0 1rem", borderRadius: "2rem", border: "1px solid var(--box-border)", background: "var(--main-bg)", fontSize: "1.3rem", width: "100%", outline: "none", cursor: "pointer" }}
                      />
                    </div>
                    <div className="field-item">
                      {/* Header: "Appointment Time  ‹ 1 / N ›" — always show stepper */}
                      <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
                        <span className="session-heading" style={{ color: "#009688" }}>
                          {t("appointmentTime") || "Appointment Time"}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "1.3rem" }}>
                          <button
                            type="button"
                            onClick={() => updateServiceSchedule(idx, { activeSlot: Math.max(0, sched.activeSlot - 1) })}
                            disabled={sched.activeSlot === 0}
                            style={{ background: "none", border: "1px solid var(--box-border)", borderRadius: "50%", width: "2.2rem", height: "2.2rem", cursor: sched.activeSlot === 0 ? "default" : "pointer", fontSize: "1.2rem", lineHeight: 1, color: sched.activeSlot === 0 ? "#bbb" : "#009688" }}
                          >‹</button>
                          <span style={{ fontWeight: 600, color: "#009688", minWidth: "3.5rem", textAlign: "center" }}>
                            {sched.activeSlot + 1} / {sched.times.length}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateServiceSchedule(idx, { activeSlot: Math.min(sched.times.length - 1, sched.activeSlot + 1) })}
                            disabled={sched.activeSlot === sched.times.length - 1}
                            style={{ background: "none", border: "1px solid var(--box-border)", borderRadius: "50%", width: "2.2rem", height: "2.2rem", cursor: sched.activeSlot === sched.times.length - 1 ? "default" : "pointer", fontSize: "1.2rem", lineHeight: 1, color: sched.activeSlot === sched.times.length - 1 ? "#bbb" : "#009688" }}
                          >›</button>
                        </div>
                      </div>
                      {/* Time pill — each field updates its own key directly, no string parsing */}
                      {(() => {
                        const slot = sched.times[sched.activeSlot] || defaultSlot();
                        const activeSlotIdx = sched.activeSlot;
                        const updateField = (field, val) => {
                          setServiceSchedules((prev) => prev.map((s, i) => {
                            if (i !== idx) return s;
                            const norm = normalizeSched(s);
                            const newTimes = norm.times.map((sl, si) => si === activeSlotIdx ? { ...sl, [field]: val } : sl);
                            return { ...norm, times: newTimes };
                          }));
                        };
                        const digits = (v) => v.replace(/\D/g, "").slice(0, 2);
                        const clampH = (v) => { const n = parseInt(v, 10); if (isNaN(n) || v === "") return v; return String(Math.min(12, Math.max(1, n))).padStart(2, "0"); };
                        const clampM = (v) => { const n = parseInt(v, 10); if (isNaN(n) || v === "") return v; return String(Math.min(59, Math.max(0, n))).padStart(2, "0"); };
                        const txt = { width: "2.8rem", border: "none", background: "transparent", fontSize: "1.3rem", outline: "none", textAlign: "center" };
                        const sel = { border: "none", background: "transparent", fontSize: "1.3rem", outline: "none", cursor: "pointer" };
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%", padding: "0 1rem", height: "3.6rem", borderRadius: "2rem", border: "1px solid var(--box-border)", background: "var(--main-bg)", boxSizing: "border-box" }}>
                            <input type="text" value={slot.sH} maxLength={2} placeholder="HH"
                              onChange={(e) => updateField("sH", digits(e.target.value))}
                              onBlur={(e) => {
                                const clamped = clampH(e.target.value);
                                updateField("sH", clamped);
                                if (sched.startDate === todayISO && clamped && slot.sM) {
                                  if (to24hMinutes(clamped, slot.sM, slot.sP) <= nowMinutes) {
                                    ToastPopup.error(t("timeMustBeFuture") || "Start time must be after current time");
                                    updateField("sH", "");
                                  }
                                }
                              }}
                              style={txt} />
                            <span style={{ fontSize: "1.3rem", fontWeight: 700 }}>:</span>
                            <input type="text" value={slot.sM} maxLength={2} placeholder="MM"
                              onChange={(e) => updateField("sM", digits(e.target.value))}
                              onBlur={(e) => updateField("sM", clampM(e.target.value))}
                              style={txt} />
                            <select value={slot.sP} onChange={(e) => updateField("sP", e.target.value)} style={sel}>
                              <option value="AM">AM</option>
                              <option value="PM">PM</option>
                            </select>
                            <span style={{ color: "#555", fontSize: "1.3rem", flexShrink: 0, margin: "0 0.2rem" }}>→</span>
                            <input type="text" value={slot.eH} maxLength={2} placeholder="HH"
                              onChange={(e) => updateField("eH", digits(e.target.value))}
                              onBlur={(e) => updateField("eH", clampH(e.target.value))}
                              style={txt} />
                            <span style={{ fontSize: "1.3rem", fontWeight: 700 }}>:</span>
                            <input type="text" value={slot.eM} maxLength={2} placeholder="MM"
                              onChange={(e) => updateField("eM", digits(e.target.value))}
                              onBlur={(e) => updateField("eM", clampM(e.target.value))}
                              style={txt} />
                            <select value={slot.eP} onChange={(e) => updateField("eP", e.target.value)} style={sel}>
                              <option value="AM">AM</option>
                              <option value="PM">PM</option>
                            </select>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Frequency */}
                  <div style={{ marginTop: "2rem", marginLeft: "2.2rem" }}>
                    <span className="session-heading">{t("frequency")}</span>
                    <div style={{ display: "flex", gap: "0.8rem", flexWrap: "nowrap", marginTop: "1rem" }}>
                      {DAY_KEYS.map((day, i) => (
                        <label key={day} style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: "1.3rem", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={sched.frequencyDays[i]}
                            onChange={() => {
                              const u = [...sched.frequencyDays];
                              u[i] = !u[i];
                              updateServiceSchedule(idx, { frequencyDays: u });
                            }}
                            style={{ accentColor: "#009688", width: "1.4rem", height: "1.4rem" }}
                          />
                          {t(day)}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Upcoming/Past: Session Info Card ── */}
        {from !== "scheduled" && session && (
          <div className="pdl-info">
            <div className="pdl-header-row">
              <p className="pdl-title">{t("sessionDetails")}</p>
              {session.dayDetail && <span style={{ fontSize: "1.3rem", color: "#009688", fontWeight: 600 }}>{session.dayDetail}</span>}
            </div>
            <div className="pdl-body grid-3-col">
              <div className="field-item">
                <span className="session-heading">{t("sessionDate")}</span>
                <div className="field-value">{formatSessionDate(session.startTime || session.date)}</div>
              </div>
              <div className="field-item">
                <span className="session-heading">{t("sessionHours")}</span>
                <div className="field-value">
                  {formatSessionTime(session.startTime)} → {formatSessionTime(session.endTime)}
                  {calcDuration(session.startTime, session.endTime) && (
                    <span style={{ marginLeft: "0.6rem", color: "#888", fontSize: "1.2rem" }}>({calcDuration(session.startTime, session.endTime)})</span>
                  )}
                </div>
              </div>
              {currentSessionServices.map((svc, idx, arr) => (
                <React.Fragment key={idx}>
                  <div className="field-item">
                    <span className="session-heading">{t("serviceGroup")}{arr.length > 1 ? ` ${idx + 1}` : ""}</span>
                    <div className="field-value">{svc?.service?.title?.[lang] || svc?.service?.title?.en || "-"}</div>
                  </div>
                  <div className="field-item">
                    <span className="session-heading">{t("subServiceGroup")}{arr.length > 1 ? ` ${idx + 1}` : ""}</span>
                    <div className="field-value">{svc?.subService?.title?.[lang] || svc?.subService?.title?.en || "-"}</div>
                  </div>
                  <div className="field-item">
                    <span className="session-heading">{t("serviceItem")}{arr.length > 1 ? ` ${idx + 1}` : ""}</span>
                    <div className="field-value">{svc?.serviceItem?.title?.[lang] || svc?.serviceItem?.title?.en || "-"}</div>
                  </div>
                </React.Fragment>
              ))}

              {(session.checkInTime || session.checkinTime || session.checkIn) && (
                <div className="field-item">
                  <span className="session-heading">{t("checkInTime")}</span>
                  <div className="field-value">{formatSessionTime(session.checkInTime || session.checkinTime || session.checkIn)}</div>
                </div>
              )}
              {(session.checkOutTime || session.checkoutTime || session.checkOut) && (
                <div className="field-item">
                  <span className="session-heading">{t("checkOutTime")}</span>
                  <div className="field-value">{formatSessionTime(session.checkOutTime || session.checkoutTime || session.checkOut)}</div>
                </div>
              )}
            </div>
            {/* {session.checkoutNotes && (
            <div style={{ marginTop: "1.2rem" }}>
              <span className="session-heading">{t("checkoutNotes")}</span>
              <div className="field-value" style={{ marginTop: "0.4rem", padding: "0.8rem 1rem", background: "var(--list-bg)", borderRadius: "0.8rem", fontSize: "1.3rem" }}>
                {session.checkoutNotes}
              </div>
            </div>
          )} */}
            {/* {session.feedback && (
            <div className="session-feedback-block">
              <div className="feedback-layout session-feedback-layout">
                <div className="feedback-col">
                  <span className="session-heading">{t("rating")}</span>
                  <div className="field-value session-rating-value">
                    <span className="star-rating-display" aria-label={`${normalizedFeedbackRating} ${t("rating")}`}>
                      {Array.from({ length: 5 }, (_, index) => (
                        <span key={index} className={`star ${index < normalizedFeedbackRating ? "filled" : ""}`} aria-hidden="true">
                          &#9733;
                        </span>
                      ))}
                    </span>
                    <span className="rating-text">{normalizedFeedbackRating ? `${normalizedFeedbackRating}/5` : "-"}</span>
                  </div>
                </div>
                <div className="feedback-col">
                  <span className="session-heading">{t("comment")}</span>
                  <div className="field-value session-comment-value">{feedbackComment}</div>
                </div>
              </div>
            </div>
          )} */}
          </div>
        )}

        {/* ── Additional Consumables ── */}
        {/* {session?.additionalConsumables?.length > 0 && (
          <div className="pdl-info">
            <div className="pdl-header-row">
              <p className="pdl-title">{t("additionalConsumables")}</p>
            </div>
            <table className="sd-consumable-table">
              <thead>
                <tr>
                  <th>{t("itemName")}</th>
                  <th>{t("quantity")}</th>
                </tr>
              </thead>
              <tbody>
                {session.additionalConsumables.map((c, i) => (
                  <tr key={c._id || i}>
                    <td>{c.consumable?.itemName?.[lang] || c.consumable?.itemName?.en || "-"}</td>
                    <td>{c.quantity ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )} */}

        {/* ── Full Request Details (patient info, attachments, description) ── */}
        {from !== "scheduled" && requestDetails && (
          <div className="pdl-info">
            <PatientPersonalInfo data={reqData} />
          </div>
        )}

        {/* Homecare Staff Details — only for upcoming/past, not scheduled, and only when a nurse is assigned */}
        {from !== "scheduled" && nurseEntry && (
          <div className="pdl-info">
            <div className="pdl-header-row">
              <p className="pdl-title">{t("nurseDetails")}</p>
              <button
                className="chat-with-nurse-btn"
                onClick={() =>
                  setChatNurseId({
                    nurseId,
                    name: displayNurseName,
                    employeeID: nurseDetails?.employeeID || nurseEntry?.employeeID,
                    profilePhoto: nurseDetails?.profilePhoto || nurseEntry?.profilePhoto,
                  })
                }
              >
                💬 {t("chatWithNurse") || "Chat with Nurse"}
              </button>
            </div>
            <div className="pdl-body">
              <div className="pdl-section-leftt">
                <img
                  src={nurseDetails?.profilePhoto || Icons.profile}
                  alt="nurse"
                  className="nurse-avatar-lg"
                  onError={(e) => {
                    e.currentTarget.src = Icons.profile;
                  }}
                />
              </div>
              <div className="pdl-section-right">
                <p className="ppi-name">{nurseLoading ? t("loading") + "…" : displayNurseName}</p>
                <div className="nurse-info-grid">
                  <div className="pdl-detail">
                    <img src={Icons.gender} alt="gender" />
                    <span>{nurseDetails?.gender ? t(nurseDetails.gender) : "-"}</span>
                  </div>
                  <div className="pdl-detail">
                    <img src={Icons.id} alt="id" />
                    <span>{nurseDetails?.employeeID || "-"}</span>
                  </div>
                  <div className="pdl-detail">
                    <img src={Icons.SmsIcon} alt="email" />
                    <span>{nurseDetails?.email || "-"}</span>
                  </div>
                  <div className="pdl-detail">
                    <img src={Icons.Call} alt="phone" />
                    <span>
                      {nurseDetails?.countryCode ? `+${nurseDetails.countryCode} ` : ""}
                      {nurseDetails?.phoneNumber || "-"}
                    </span>
                  </div>
                  {nurseDetails?.address && (
                    <div className="pdl-detail nurse-address-row">
                      <img src={Icons.Location} alt="address" />
                      <span>
                        {typeof nurseDetails.address === "string"
                          ? nurseDetails.address
                          : nurseDetails.address?.en ||
                            nurseDetails.address?.vi ||
                            [nurseDetails.address?.street, nurseDetails.address?.ward, nurseDetails.address?.city].filter(Boolean).join(", ") ||
                            "-"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* Payment Details — upcoming/past only, shown when payment has been collected */}
      {from !== "scheduled" && requestDetails?.paymentDetails?.status === "PAID" && (() => {
        const pd = requestDetails.paymentDetails;
        const collectedBy = pd.collectedBy;
        const collectorName = collectedBy
          ? `${resolveLocalized(collectedBy.firstName)} ${resolveLocalized(collectedBy.lastName)}`.trim()
          : "-";
        const paidAtFormatted = pd.paidAt
          ? `${new Date(pd.paidAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} at ${new Date(pd.paidAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`
          : "-";
        const isOnlineMethod = String(pd.method || "").toUpperCase() === "ONLINE";
        return (
          <div className="pdl-info">
            <div className="pdl-header-row">
              <p className="pdl-title">{t("paymentDetails") || "Payment Details"}</p>
              <span style={{ background: "#e6fff5", color: "#00b894", border: "1px solid #00b894", borderRadius: "2rem", padding: "0.3rem 1.2rem", fontSize: "1.2rem", fontWeight: 600 }}>
                {pd.status}
              </span>
            </div>
            <div className="pdl-body grid-3-col">
              <div className="field-item">
                <span className="session-heading">{t("amount") || "Amount"}</span>
                <div className="field-value">{pd.currencySymbol || ""}{Number(pd.amount || 0).toLocaleString("vi-VN")}</div>
              </div>
              <div className="field-item">
                <span className="session-heading">{t("paymentMethod") || "Method"}</span>
                <div className="field-value">{pd.method || "-"}</div>
              </div>
              <div className="field-item">
                <span className="session-heading">{t("paidAt") || "Paid At"}</span>
                <div className="field-value">{paidAtFormatted}</div>
              </div>
              {pd.reason && (
                <div className="field-item">
                  <span className="session-heading">{t("reason") || "Reason"}</span>
                  <div className="field-value" style={{ textTransform: "capitalize" }}>{pd.reason}</div>
                </div>
              )}
              {!isOnlineMethod && (
                <div className="field-item">
                  <span className="session-heading">{t("collectedBy") || "Collected By"}</span>
                  <div className="field-value">{collectorName}{collectedBy?.employeeId ? ` (${collectedBy.employeeId})` : ""}</div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Homecare Staff Observations — upcoming/past only */}
      {hasNurseObservations && (
        <div className="pdl-info nurse-observations-card">
          <div className="pdl-header-row">
            <p className="pdl-title">{t("nursesObservations")}</p>
            {(observationImages.length > 0 || nurseNotesText) && (
  <button 
    type="button" 
    className="download-btn-text"
    onClick={handleDownloadObservations}
  >
    {t("download")}
  </button>
)}
          </div>
          <div className="pdl-body column-layout">
            {observationImages.length > 0 ? (
              <div className="observation-carousel">
                <div className="carousel-track">
                  {observationImages.map((img, idx) => (
                    <button key={idx} type="button" className="obs-thumb-button" onClick={() => openSecureFileUrl(img)} aria-label={t("nursesObservations")}>
                      <img src={img} alt={t("nursesObservations")} className="obs-thumb" onError={(e) => { e.currentTarget.src = Icons.profileimg; }} />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="no-observations-text" style={{ fontSize: "1.3rem", color: "#888", padding: "1rem 0" }}>
                {t("noObservationAttachmentsAdded", "No observation attachments added")}
              </div>
            )}
            <div className="notes-container">
              <div className="green-bar" />
              <div className="notes-content">
                <h4 className="notes-title">{t("nurseNotes")}</h4>
                <p className="notes-text">{nurseNotesText || "-"}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Nurse Reassignment History — upcoming session details only */}
      {from === "upcoming" && (
        <div className="pdl-info">
          <p className="pdl-title">{t("reassignmentHistory")}</p>
          <div className="reallocation-table-wrap">
            <table className="ppl-table">
              <thead className="ppl-thead">
                <tr>
                  <th>{t("reassignedAt")}</th>
                  <th>{t("originalNurses")}</th>
                  <th>{t("replacementNurses")}</th>
                  <th>{t("reassignedBy")}</th>
                </tr>
              </thead>
              <tbody className="ppl-tbody">
                {reassignHistoryLoading && (
                  <tr>
                    <td colSpan={4} className="session-empty-cell">{t("loading")}…</td>
                  </tr>
                )}
                {!reassignHistoryLoading && reassignHistory.length === 0 && (
                  <tr>
                    <td colSpan={4} className="session-empty-cell">{t("noRecordFound")}</td>
                  </tr>
                )}
                {!reassignHistoryLoading && reassignHistory.map((entry, index) => (
                  <tr key={entry._id || index} className="ppl-table-row reallocation-row">
                    <td>{formatHistoryDateTime(entry.createdAt)}</td>
                    <td>{formatNurseNames(entry.originalNurses)}</td>
                    <td>{formatNurseNames(entry.replacementNurses)}</td>
                    <td>
                      <span className={`reallocation-by-badge ${entry.reassignedByModel === "Admin" ? "by-admin" : "by-nurse"}`}>
                        {resolveLocalized(entry.reassignedBy?.firstName) ? `${resolveLocalized(entry.reassignedBy.firstName)} ${resolveLocalized(entry.reassignedBy.lastName)}`.trim() : entry.reassignedByModel || "-"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

        {/* Feedbacks — upcoming/past only */}
        {from !== "scheduled" && (sessionUserRating || sessionRating) && (
          <div className="pdl-info">
            <p className="pdl-title">{t("feedbacks")}</p>
            <div style={{ display: "flex", gap: "0", width: "100%", alignItems: "flex-start" }}>
              {/* LEFT: Patient → Homecare Staff Feedback */}
              <div style={{ flex: 1, paddingRight: "2.4rem" }}>
                <p style={{ fontWeight: 700, fontSize: "1.5rem", marginBottom: "0.2rem" }}>{t("patient")}</p>
                <p style={{ fontSize: "1.2rem", color: "#888", marginBottom: "1.6rem" }}>{t("feedback")}</p>

                {sessionUserRating ? (
                  <>
                    {/* Metric rows */}
                    {[
                      { key: "communication", label: t("communication") },
                      { key: "compassionEmpathy", label: t("compassionEmpathy") },
                      { key: "knowledge", label: t("knowledge") },
                      { key: "professionalism", label: t("professionalism") },
                      { key: "reliability", label: t("reliability") },
                      { key: "skillsCompetence", label: t("skillsCompetence") },
                    ].map(({ key, label }) => (
                      <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", gap: "1rem" }}>
                        <span style={{ fontSize: "1.3rem", color: "#555", flex: 1 }}>{label}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                          <div style={{ display: "flex", gap: "0.2rem" }}>
                            {Array.from({ length: 5 }, (_, i) => (
                              <span key={i} style={{ fontSize: "1.6rem", color: i < Math.min(Math.max(Number(sessionUserRating[key]) || 0, 0), 5) ? "#f5a623" : "#ddd" }}>
                                &#9733;
                              </span>
                            ))}
                          </div>
                          <span style={{ fontSize: "1.3rem", fontWeight: 600, color: "#333", minWidth: "2.5rem" }}>{sessionUserRating[key] ?? "-"}/5</span>
                        </div>
                      </div>
                    ))}

                    {/* Note */}
                    <div style={{ marginTop: "1.2rem" }}>
                      <p style={{ fontWeight: 700, fontSize: "1.4rem", marginBottom: "0.6rem" }}>{t("comment")}</p>
                      <p style={{ fontSize: "1.3rem", color: sessionUserRating.note ? "#444" : "#aaa", lineHeight: 1.6 }}>{sessionUserRating.note || t("noCommentProvided")}</p>
                    </div>
                  </>
                ) : (
                  <p style={{ fontSize: "1.3rem", color: "#aaa" }}>{t("noRatingProvided")}</p>
                )}
              </div>

              {/* Divider */}
              <div style={{ width: "2px", background: "#009688", borderRadius: "2px", alignSelf: "stretch", flexShrink: 0 }} />

              {/* RIGHT: Nurse → Session Rating */}
              <div style={{ flex: 1, paddingLeft: "2.4rem" }}>
                <p style={{ fontWeight: 700, fontSize: "1.5rem", marginBottom: "0.2rem" }}>{t("homecareStaff")}</p>
                <p style={{ fontSize: "1.2rem", color: "#888", marginBottom: "1.6rem" }}>{t("feedback")}</p>

                {sessionRating ? (
                  <>
                    {/* Metric rows */}
                    {[
                      { key: "patientSatisfaction", label: t("patientSatisfaction") },
                      { key: "anxietyLevel", label: t("anxietyLevel") },
                      { key: "clearConnection", label: t("clearConnection") },
                      { key: "culturalSensitivity", label: t("culturalSensitivity") },
                      { key: "familyInvolvement", label: t("familyInvolvement") },
                      { key: "treatmentAdherence", label: t("treatmentAdherence") },
                    ].map(({ key, label }) => (
                      <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", gap: "1rem" }}>
                        <span style={{ fontSize: "1.3rem", color: "#555", flex: 1 }}>{label}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                          <div style={{ display: "flex", gap: "0.2rem" }}>
                            {Array.from({ length: 5 }, (_, i) => (
                              <span key={i} style={{ fontSize: "1.6rem", color: i < Math.min(Math.max(Number(sessionRating[key]) || 0, 0), 5) ? "#f5a623" : "#ddd" }}>
                                &#9733;
                              </span>
                            ))}
                          </div>
                          <span style={{ fontSize: "1.3rem", fontWeight: 600, color: "#333", minWidth: "2.5rem" }}>{sessionRating[key] ?? "-"}/5</span>
                        </div>
                      </div>
                    ))}

                    {/* Note */}
                    <div style={{ marginTop: "1.2rem" }}>
                      <p style={{ fontWeight: 700, fontSize: "1.4rem", marginBottom: "0.6rem" }}>{t("comment")}</p>
                      <p style={{ fontSize: "1.3rem", color: sessionRating.note ? "#444" : "#aaa", lineHeight: 1.6 }}>{sessionRating.note || t("noCommentProvided")}</p>
                    </div>
                  </>
                ) : (
                  <p style={{ fontSize: "1.3rem", color: "#aaa" }}>{t("noRatingProvided")}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Save — only shown for the scheduled tab; removed for upcoming and past */}
        <div className="form-section-footer" style={{ marginTop: "2rem", paddingRight: "2rem" }}>
          <button className="form-section-cancel session-back-btn" onClick={handleBack}>
            {t("back")}
          </button>
          {from !== "past" && from !== "upcoming" && (
            <button className="form-section-create" onClick={handleSave}>
              {t("save")}
            </button>
          )}
        </div>
      </div>
      <ChatWidget openConversation={chatNurseId} onOpenConversationHandled={() => setChatNurseId(null)} />
    </>
  );
};

export default SessionDetailsPage;
